import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const TenantOnboardingSimple = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
    step5: false
  });

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  // Mark onboarding as complete when reaching step 6
  const markOnboardingComplete = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      await fetch(`${API_URL}/api/profiles/tenant/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }
  }, []);

  // When we reach step 6 (completion screen), mark onboarding as complete
  useEffect(() => {
    if (currentStep === 6) {
      markOnboardingComplete();
    }
  }, [currentStep, markOnboardingComplete]);

  const handleTestConfirm = async (step) => {
    // Simulate successful upload without actually uploading files
    setLoading(true);
    
    try {
      // Just wait a bit to simulate processing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mark step as completed
      setUploadedDocs(prev => ({...prev, [`step${step}`]: true}));
      toast.success(`✓ Step ${step} marked as complete (TEST MODE)`);
      
      // Automatically move to next step
      setTimeout(() => {
        if (step < 5) {
          setCurrentStep(step + 1);
        } else {
          setCurrentStep(6); // Complete
        }
      }, 500);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to confirm step');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (step, files) => {
    if (!files || files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setLoading(true);
    
    try {
      const token = getToken();
      if (!token) {
        toast.error('Please log in again');
        navigate('/login');
        return;
      }

      const formData = new FormData();
      
      // Add files based on step
      if (step === 1) {
        // Profile info - just mock for now
        Array.from(files).forEach(file => {
          formData.append('id_docs', file);
        });
      } else if (step === 2) {
        // KYC documents
        Array.from(files).forEach(file => {
          formData.append('id_docs', file);
        });
        // Add a dummy selfie if not provided
        if (files.length === 1) {
          formData.append('selfie', files[0]);
        } else {
          formData.append('selfie', files[1]);
        }
      } else if (step === 3) {
        // Right to rent
        Array.from(files).forEach(file => {
          formData.append('documents', file);
        });
      } else if (step === 4) {
        // Employment documents - we'll just mark as uploaded
      } else if (step === 5) {
        // References - we'll just mark as uploaded
      }

      let endpoint = '';
      let method = 'POST';
      
      // Map steps to API endpoints
      switch(step) {
        case 1:
          // Profile completion - for now, just mark as done
          await new Promise(resolve => setTimeout(resolve, 800));
          break;
        case 2:
          endpoint = `${API_URL}/api/onboarding/tenant/kyc`;
          break;
        case 3:
          endpoint = `${API_URL}/api/onboarding/tenant/right-to-rent`;
          break;
        case 4:
          // Employment info - for now, just mark as done
          await new Promise(resolve => setTimeout(resolve, 800));
          break;
        case 5:
          // Credit consent - just mark as done
          await new Promise(resolve => setTimeout(resolve, 800));
          break;
      }

      // Call backend API if endpoint is defined
      if (endpoint) {
        const response = await fetch(endpoint, {
          method: method,
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Upload failed');
        }

        const data = await response.json();
        console.log(`Step ${step} response:`, data);
      }
      
      // Mark step as completed
      setUploadedDocs(prev => ({...prev, [`step${step}`]: true}));
      toast.success(`✓ Step ${step} completed successfully!`);
      
      // Automatically move to next step
      setTimeout(() => {
        if (step < 5) {
          setCurrentStep(step + 1);
        } else {
          setCurrentStep(6); // Complete
        }
      }, 500);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      number: 1,
      title: "Profile Information",
      description: "Upload personal information documents",
      documents: ["Proof of Identity (Passport/Driver's License)", "Proof of Address (Utility Bill)", "Photo for Profile"]
    },
    {
      number: 2,
      title: "Identity Verification (KYC)",
      description: "Upload documents for identity verification",
      documents: ["Valid Photo ID (Passport/Driver's License)", "Selfie for Verification", "Recent Utility Bill"]
    },
    {
      number: 3,
      title: "Right to Rent",
      description: "Upload documents proving your right to rent in the UK",
      documents: ["Passport or Visa", "BRP (Biometric Residence Permit) if applicable", "Share Code (if applicable)"]
    },
    {
      number: 4,
      title: "Employment Information",
      description: "Upload employment and income verification",
      documents: ["Employment Contract or Offer Letter", "Last 3 Months Payslips", "Bank Statements (last 3 months)"]
    },
    {
      number: 5,
      title: "References & Credit Check",
      description: "Provide references and consent for credit check",
      documents: ["Previous Landlord Reference", "Employer Reference", "Credit Check Consent Form"]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Tenant Onboarding</h1>
        <p className="text-gray-600 mb-8">Complete all 5 steps to activate your account</p>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4, 5].map(step => (
              <div key={step} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  uploadedDocs[`step${step}`] 
                    ? 'bg-green-600 text-white'
                    : currentStep === step 
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {uploadedDocs[`step${step}`] ? '✓' : step}
                </div>
                <span className="text-xs mt-1">{uploadedDocs[`step${step}`] ? 'Done' : `Step ${step}`}</span>
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{width: `${(Object.values(uploadedDocs).filter(Boolean).length / 5) * 100}%`}}
            ></div>
          </div>
        </div>

        {/* Steps */}
        {currentStep <= 5 && (
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-2xl font-bold">Step {currentStep}: {steps[currentStep - 1].title}</h2>
              {uploadedDocs[`step${currentStep}`] && <span className="text-green-600 text-3xl">✓</span>}
            </div>
            
            <p className="text-gray-600 mb-6">{steps[currentStep - 1].description}</p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-2">Required Documents:</h3>
              <ul className="list-disc list-inside space-y-1">
                {steps[currentStep - 1].documents.map((doc, idx) => (
                  <li key={idx} className="text-gray-700">{doc}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Upload Documents</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(currentStep, e.target.files)}
                  disabled={loading}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Accepted: PDF, JPG, PNG (max 10MB each)</p>
              </div>

              {loading && (
                <div className="text-center py-2">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-600 mt-2">Uploading...</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                {currentStep > 1 && (
                  <Button 
                    variant="outline" 
                    onClick={() => setCurrentStep(currentStep - 1)}
                    disabled={loading}
                  >
                    ← Back
                  </Button>
                )}
                <Button 
                  onClick={() => handleTestConfirm(currentStep)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  disabled={loading}
                >
                  ✓ TEST CONFIRM
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    // Skip without marking as complete
                    if (currentStep < 5) {
                      setCurrentStep(currentStep + 1);
                    } else {
                      setCurrentStep(6); // Go to completion screen
                    }
                  }}
                  className="flex-1"
                  disabled={loading}
                >
                  Skip for Now →
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Complete */}
        {currentStep === 6 && (
          <Card className="p-8 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold mb-2">Steps Completed!</h2>
            <p className="text-gray-600 mb-6">
              {Object.values(uploadedDocs).filter(Boolean).length > 0 
                ? "Your documents have been submitted for review. We'll notify you once verified."
                : "You've skipped all steps. Please upload documents to complete verification."}
            </p>
            
            <div className={`${Object.values(uploadedDocs).filter(Boolean).length > 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4 mb-6`}>
              <h3 className={`font-semibold ${Object.values(uploadedDocs).filter(Boolean).length > 0 ? 'text-green-800' : 'text-yellow-800'} mb-2`}>
                {Object.values(uploadedDocs).filter(Boolean).length > 0 ? 'Documents Submitted:' : 'Steps Skipped:'}
              </h3>
              <div className="flex justify-center gap-4 flex-wrap">
                {Object.entries(uploadedDocs).map(([key, value]) => (
                  value && (
                    <div key={key} className="text-green-600">
                      ✓ Step {key.replace('step', '')}
                    </div>
                  )
                ))}
                {Object.values(uploadedDocs).filter(Boolean).length === 0 && (
                  <p className="text-yellow-700">No documents uploaded yet</p>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                ← Back to Steps
              </Button>
              <Button onClick={() => navigate('/tenant/dashboard')}>
                Go to Dashboard →
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TenantOnboardingSimple;
